import Vue from 'vue'
import _ from 'lodash'
import axios from 'axios'
import {Raven} from 'vue-raven'
import {DocumentsGroupBy} from '@/views/documents/types/group-by/inex'
import {AppDocSystemTypes, getDocSystemTypeDefaultModel} from '@/types/document/types'
import {fetchDocTypeViewDocsStats} from '@/views/documents/types/views/stats'
import {getDocTypeFilter, getStatusesFilter} from '@/views/documents/types/views/search'
import {getAppDocUnknownStatusModel} from '@/types/document/status'
import {documentTypesApiService} from '@/api/document/types'
import {demoContractApi} from '@/api/document/demo-contracts'
import {getDocumentStatusesInGroup, getDocumentTypesInGroup} from '@/views/sidebars/documents/groups'

export const DOCS_GROUPS_SET_SELECTED = 'DOCS_GROUPS_SET_SELECTED'
// Document types
export const DOCS_TYPES_ADD = 'DOCS_TYPES_ADD'
export const DOCS_TYPES_FETCH = 'DOCS_TYPES_FETCH'
export const DOCS_TYPES_SET = 'DOCS_TYPES_SET'
export const DOCS_TYPES_REMOVE = 'DOCS_TYPES_REMOVE'
export const DOCS_TYPES_SELECTED_SET = 'DOCS_TYPES_SELECTED_SET'
export const DOCS_TYPES_SELECTED_UPDATE_PROP = 'DOCS_TYPES_SELECTED_UPDATE_PROP'
export const DOCS_TYPES_SELECTED_UPDATE_STATUSES = 'DOCS_TYPES_SELECTED_UPDATE_STATUSES'
export const DOCS_TYPES_SET_LOADING = 'DOCS_TYPES_SET_LOADING'

export const DOCS_FETCH_SUBTYPES_FOR_SELECTED_TYPE = 'DOCS_FETCH_SUBTYPES_FOR_SELECTED_TYPE'
export const DOCS_SET_SUBTYPES_FOR_SELECTED_TYPE = 'DOCS_SET_SUBTYPES_FOR_SELECTED_TYPE'
// Documents filter
export const DOCS_GET_STATS_FOR_BASE_FILTER = 'DOCS_GET_STATS_FOR_BASE_FILTER'
export const DOCS_SET_MAX_COUNT = 'DOCS_SET_MAX_COUNT'
export const DOCS_SET_STATS_PROP = 'DOCS_SET_STATS_PROP'
export const DOCS_SET_SEARCH_STRING = 'DOCS_SET_SEARCH_STRING'
export const DOCS_SET_SORT_BY_FIELD = 'DOCS_SET_SORT_BY_FIELD'
export const DOCS_SET_GROUP_BY_FIELD = 'DOCS_SET_GROUP_BY_FIELD'
export const DOCS_SET_SORT_BY_SET_IS_ASCENDING = 'DOCS_SET_SORT_BY_SET_IS_ASCENDING'
export const DOCS_SET_ADVANCED_FILTER = 'DOCS_SET_ADVANCED_FILTER'

export const DOCS_BACK_STACK_PUSH = 'DOCS_BACK_STACK_PUSH'
export const DOCS_BACK_STACK_POP = 'DOCS_BACK_STACK_POP'

export const DOCS_SET_DATA_ENTRY_MODE = 'DOCS_SET_DATA_ENTRY_MODE'

const initialState = () => ({
    documentGroupSelected: null,

    documentTypes: [],
    documentTypeSelected: null,
    selectedDocumentTypeSubtypes: [],
    documentsBackStack: [], // for document viewer modal
    documentsMaxCount: 0, // with base filters applied (like team, statuses, location, see below)

    // ES
    searchString: '',
    sortBy: 'dateInv',
    stats: {
        supplier_counts: {}, // [supplier.uuid]: [count]
        users_counts: {}, // [user.uuid]: [count]
        count_by_status: {} // [status.uuid]: [count]
    },
    isAscendingSort: false,
    isDocTypesLoading: false,
    advancedFilter: [],

    // staff only
    hasDataEntryModeEnabled: false
})

const state = initialState()

export const getters = {
    documentGroupSelected: state => state.documentGroupSelected,
    documentTypesInSelectedGroup: (state, getters) => getDocumentTypesInGroup(getters.documentGroupSelected),
    documentStatusesInSelectedGroup: (state, getters) => getDocumentStatusesInGroup(getters.documentGroupSelected),

    documentTypes: state => state.documentTypes,
    documentsBackStack: state => state.documentsBackStack,
    documentsMaxCount: state => state.documentsMaxCount,
    documentTypeSelected: state => state.documentTypeSelected || {},

    selectedDocumentTypeSubtypes: state => state.selectedDocumentTypeSubtypes || [],
    selectedDocumentTypeSubtypesEnabled: (state, getters) => {
        if (getters.documentGroupSelected) {
            return getters.selectedDocumentTypeSubtypes.filter(s => getters.documentTypesInSelectedGroup.includes(s.property))
        }
        return getters.selectedDocumentTypeSubtypes
    },
    dtStatuses: (state, getters) => {
        return _.sortBy(_.uniqBy(_.get(getters.documentTypeSelected, 'statuses', []), 'key'), 'order')
    },
    dtStatusesEnabled: (state, getters) => {
        const enabledStatuses = getters.dtStatuses.filter(s => s.isEnabled)
        if (getters.documentGroupSelected) {
            return enabledStatuses.filter(s => getters.documentStatusesInSelectedGroup.includes(s.key))
        }
        return enabledStatuses
    },
    dtStatusesSelected: (state, getters) => getters.dtStatusesEnabled.filter(s => s.isSelected),
    sortBy: state => state.sortBy,
    groupBy: state => state.groupBy || DocumentsGroupBy.STATUS,
    searchString: state => state.searchString,
    advancedFilter: state => state.advancedFilter,
    hasDataEntryModeEnabled: state => !!state.hasDataEntryModeEnabled,

    isSystemDocs: (state, getters) => getters.documentTypeSelected.isSystem,
    isSaleDocs: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.SALES,
    isPurchaseDocs: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.PURCHASING,
    isStatementDocs: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.STATEMENTS,
    isBatchDocs: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.BATCHES,
    isCustomDocs: (state, getters) => !getters.isSystemDoc,

    isDemoItems: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.DEMO_ITEMS,
    isDemoSuppliers: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.DEMO_SUPPLIERS,
    isDemoContracts: (state, getters) => getters.documentTypeSelected.type === AppDocSystemTypes.DEMO_CONTRACTS,

    dtSectionModel: (state, getters) => getDocSystemTypeDefaultModel(getters.documentTypeSelected.type),

    isAscendingSort: state => state.isAscendingSort,
    isDocTypesLoading: state => state.isDocTypesLoading,

    countStatsByContact: state => state.stats.supplier_counts,
    countStatsByStatus: state => state.stats.count_by_status,
    countStatsByUser: state => state.stats.users_counts,

    getMemberDocsCount: (state, getters) => uuid => _.get(getters.countStatsByUser, uuid, 0),
    getContactDocsCount: (state, getters) => uuid => _.get(getters.countStatsByContact, uuid, 0),

    getDocumentTypeModel: (state, getters) => doc => {
        let dt = getters.documentTypes.find(el => doc && el.uuid === doc.type) // by uuid
        if (!dt) { // system doc
            dt = getters.documentTypes.find(el => doc && el.type === doc.section) // by key: STATEMENTS | PURCHASING | SALES
        }
        return dt || getters.documentTypeSelected || {}
    },
    getDocumentSubtypeModel: (state, getters) => subtype => getters.selectedDocumentTypeSubtypes.find(s => s.property === subtype), // AppDocSubtype

    getDocumentStatusModel: (state, getters) => doc => {
        return _.get(getters.getDocumentTypeModel(doc), 'statuses', [])
            .find(s => s.key === doc.status) || getAppDocUnknownStatusModel()
    },

    getDtStatusModelByKey: (state, getters) => key => {
        return getters.dtStatuses.find(s => s.key === key) || getAppDocUnknownStatusModel()
    },

    searchParamsLocationIds: (state, getters, rootState, rootGetters) => {
        let ids = []
        if (rootGetters.selectedLocationUuid) {
            ids = [rootGetters.selectedLocationUuid]
        } else if (rootGetters.userLocations.length !== 0) {
            ids = rootGetters.userLocations.map(l => l.uuid)
        }
        return ids
    },

    searchParamsSubsidiaryIds: (state, getters, rootState, rootGetters) => {
        let ids = []
        if (rootGetters.selectedSubsidiaryUuid) {
            ids = [rootGetters.selectedSubsidiaryUuid]
        } else if (rootGetters.userSubsidiaries.length !== 0) {
            ids = rootGetters.userSubsidiaries.map(l => l.uuid)
        }
        return ids
    }
}

export const actions = {
    [DOCS_GET_STATS_FOR_BASE_FILTER]: async({commit, getters}) => {
        if (getters.isDemoContracts) {
            const {data, error} = await demoContractApi.fetchAll()
            if (error) {
                return
            }
            const count = data.filter(doc => getters.dtStatusesSelected.some(s => s.key === doc.status)).length
            commit(DOCS_SET_MAX_COUNT, count)
            const countByStatus = {}
            getters.dtStatusesSelected.forEach(s => {
                countByStatus[s.key] = data.filter(doc => doc.status === s.key).length
            })
            commit(DOCS_SET_STATS_PROP, {prop: 'count_by_status', data: countByStatus})
            return
        }

        try {
            const stats = await fetchDocTypeViewDocsStats({
                boolQueryFilters: [
                    getStatusesFilter(),
                    getDocTypeFilter()
                ]
            })
            commit(DOCS_SET_MAX_COUNT, stats._total)
            commit(DOCS_SET_STATS_PROP, {prop: 'supplier_counts', data: stats.contacts})
            commit(DOCS_SET_STATS_PROP, {prop: 'users_counts', data: stats.users})
            commit(DOCS_SET_STATS_PROP, {prop: 'count_by_status', data: stats.statuses})
        } catch (e) {
            if (!axios.isCancel(e)) {
                commit(DOCS_SET_MAX_COUNT, 0)
            } else {
                Raven.captureException(e)
            }
        }
    },

    [DOCS_TYPES_FETCH]: ({commit}) => {
        commit(DOCS_TYPES_SET_LOADING, true)
        return axios.get('documents/types', {requestId: DOCS_TYPES_FETCH})
            .then(res => {
                commit(DOCS_TYPES_SET, _.get(res, 'data.data', []))
            })
            .finally(() => {
                commit(DOCS_TYPES_SET_LOADING, false)
            })
    },

    [DOCS_FETCH_SUBTYPES_FOR_SELECTED_TYPE]: async({commit, getters}) => {
        if (!_.get(getters.documentTypeSelected, 'type')) {
            return
        }
        const {data} = await documentTypesApiService.fetchSubtypes(getters.documentTypeSelected.type)
        commit(DOCS_SET_SUBTYPES_FOR_SELECTED_TYPE, data)
    },

    [DOCS_TYPES_ADD]: ({commit, getters}, data) => {
        return axios.post('documents/types', data)
            .then(res => {
                commit(DOCS_TYPES_SET, getters.documentTypes.concat(res.data.data))
            })
    },

    [DOCS_TYPES_REMOVE]: ({commit, getters}, uuid, forceDelete) => {
        let url = `documents/types/${uuid}`
        if (forceDelete) {
            url = `documents/types/${uuid}/force`
        }
        return axios.delete(url)
            .then(() => {
                const idx = getters.documentTypes.findIndex(d => d.uuid === uuid)
                getters.documentTypes.splice(idx, 1)
                commit(DOCS_TYPES_SET, getters.documentTypes)
            })
    },
    [DOCS_TYPES_SELECTED_SET]: ({commit, getters, rootGetters}, dt) => {
        commit(DOCS_TYPES_SELECTED_SET, dt)
    }
}

export const mutations = {
    [DOCS_GROUPS_SET_SELECTED]: (state, groupKey) => {
        state.documentGroupSelected = groupKey
    },
    [DOCS_TYPES_SET]: (state, list) => {
        state.documentTypes = list
    },
    [DOCS_SET_SUBTYPES_FOR_SELECTED_TYPE]: (state, list) => {
        state.selectedDocumentTypeSubtypes = list
    },
    [DOCS_TYPES_SELECTED_SET]: (state, item) => {
        state.documentTypeSelected = _.cloneDeep(item)
    },
    [DOCS_TYPES_SELECTED_UPDATE_PROP]: (state, {path, data}) => {
        state.documentTypeSelected = _.cloneDeep(_.set(state.documentTypeSelected, path, data))
    },
    [DOCS_TYPES_SELECTED_UPDATE_STATUSES]: (state, statuses) => {
        Vue.set(state.documentTypeSelected, 'statuses', statuses)
    },
    [DOCS_TYPES_SET_LOADING]: (state, value) => {
        state.isDocTypesLoading = value
    },
    [DOCS_SET_MAX_COUNT]: (state, count) => {
        state.documentsMaxCount = count
    },
    [DOCS_SET_SEARCH_STRING]: (state, value) => {
        state.searchString = value
    },
    [DOCS_SET_SORT_BY_FIELD]: (state, value) => {
        state.sortBy = value
    },
    [DOCS_SET_GROUP_BY_FIELD]: (state, value) => {
        state.groupBy = value
    },
    [DOCS_SET_SORT_BY_SET_IS_ASCENDING]: (state, value) => {
        state.isAscendingSort = value
    },
    [DOCS_SET_ADVANCED_FILTER]: (state, filter) => {
        state.advancedFilter = filter
    },
    [DOCS_SET_STATS_PROP]: (state, {prop, data}) => {
        state.stats[prop] = data
    },
    [DOCS_BACK_STACK_PUSH]: (state, doc) => {
        state.documentsBackStack = state.documentsBackStack.concat(doc)
    },
    [DOCS_BACK_STACK_POP]: (state) => {
        if (state.documentsBackStack.length !== 0) {
            state.documentsBackStack.splice(state.documentsBackStack.length - 1, 1)
        }
    },
    [DOCS_SET_DATA_ENTRY_MODE]: (state, status) => {
        state.hasDataEntryModeEnabled = Boolean(status)
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
